import { ArrowLeft } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import LanguageToggleLink from "../components/LanguageToggleLink";
import Seo from "../components/Seo";
import ScandicDevLogo from "../components/ScandicDevLogo";
import { useLanguage } from "../context/LanguageContext";
import { getAlternateLanguagePath, getHomePath, legalPageSeo } from "../lib/site";

const contentDE = {
  title: "KI-Ethik-Kodex",
  back: "Zurück",
  lastUpdated: "Stand: 01. Januar 2026",
  content: `
## KI-Ethik-Kodex von SCANDICDEV powered "Scandic Pilot" Bibliothek – Schwerpunkte & Direktlinks

- 1. Präambel & Geltungsbereich
- 2. Grundwerte & Leitprinzipien
- 3. Governance & Verantwortlichkeiten (AI-Ethics-Board, RACI)
- 4. Rechts- & Normenrahmen (EU-AI-Act, DSGVO, DSA, Urheberrecht, Gewerberecht)
- 5. Risiko-Klassifikation & AI-Impact-Assessment (AIIA)
- 6. Datenethik & Datenschutz (Rechtsgrundlagen, DPIA, Cookies, Drittland)
- 7. Modell- & Datenlebenszyklus (ML-Lifecycle, Data Cards, Model Cards)
- 8. Transparenz, Erklärbarkeit & Nutzerhinweise
- 9. Human-in-the-Loop & Aufsichtspflichten
- 10. Sicherheit, Robustheit & Red-Teaming (Prompt-Injection, Jailbreaks)
- 11. Lieferkette, Menschenrechte & faire Arbeit (Modern Slavery, LkSG-analog)
- 12. Bias-Management, Fairness & Inklusion (gefährdete Kunden, Barrierefreiheit)
- 13. Generative KI, Herkunftsnachweise & Kennzeichnung (C2PA, Wasserzeichen)
- 14. Inhalte, Moderation & DSA-Prozesse (Meldung, Beschwerde, Transparenz)
- 15. Domänenspezifischer Einsatz (News, Data, Health, Aviation, Yachts, Estate, Pay/Trade/Trust/Coin, Cars)
- 16. Drittparteien, Beschaffung & Vendor-Risk-Management
- 17. Betrieb, Observability, Notfall- & Wiederanlaufpläne
- 18. Vorfälle & Abhilfe (Ethik, Datenschutz, Sicherheit)
- 19. Metriken, KPIs & Assurance (intern/extern)
- 20. Schulung, Awareness & Kulturwandel
- 21. Umsetzung & Roadmap (0–6 / 6–12 / 12–24 Monate)
- 22. Rollen & RACI-Matrix (Auszug)
- 23. Checklisten (AIIA-Kurz, Datenfreigabe, Go-Live-Gate)
- 24. Formblätter & Vorlagen (Model Card, Data Card, Incident Report)
- 25. Glossar (Auszug) & Referenzen

---

## 1. Präambel & Geltungsbereich

Dieser Kodex legt verbindliche Grundsätze, Prozesse und Kontrollen für Entwicklung, Beschaffung, Betrieb und Nutzung von KI in der LEGIER Gruppe by SCANDIC DATA fest. Er gilt konzernweit für Mitarbeitende, Führungskräfte, Auftragsverarbeiter, Lieferanten und Partner. Er integriert vorhandene Konzernrichtlinien (Datenschutz, Digitale-Dienste-Prozesse, Corporate-Governance, Nachhaltigkeit, Menschenrechtspolitik, Modern-Slavery-Statement) und erweitert sie um KI-spezifische Anforderungen. Ziel ist es, Nutzen und Innovation zu ermöglichen, Risiken beherrschbar zu machen und die Rechte von Nutzer:innen, Kund:innen und der Allgemeinheit zu wahren.

---

## 2. Grundwerte & Leitprinzipien

Menschenwürde und Grundrechte stehen über ökonomischer Effizienz. KI dient Menschen – niemals umgekehrt.

**Rechtskonformität:** Einhaltung von EU-AI-Act, DSGVO, DSA sowie sektorspezifischer Normen. Keine Nutzung verbotener Praktiken.

**Verantwortung & Rechenschaft:** Für jedes KI-System ist ein verantwortlicher Owner benannt; Entscheidungen sind nachvollziehbar und anfechtbar.

**Verhältnismäßigkeit:** Ausgewogenheit von Zweck, Risiko, Eingriffsintensität und gesellschaftlichen Auswirkungen.

**Transparenz & Erklärbarkeit:** Angemessene Hinweise, Dokumentation und Kommunikationswege zu Funktionsweise, Datenlagen und Grenzen.

**Fairness & Inklusion:** Systematische Bias-Prüfung, Schutz vulnerabler Gruppen, Zugänglichkeit und Mehrsprachigkeit.

**Sicherheit & Resilienz:** Security-by-Design, Defense-in-Depth, kontinuierliche Härtung und Überwachung.

**Nachhaltigkeit:** Effizienz der Modelle und Rechenzentren (Energie, PUE/CFE), Lebenszyklus-Sicht auf Daten/Modelle.

---

## 3. Governance & Verantwortlichkeiten (AI-Ethics-Board, RACI)

**AI-Ethics-Board (AIEB):** Interdisziplinär (Tech, Recht/Compliance, Datenschutz, Sicherheit, Redaktion/Produkt, People). Aufgaben: Grundsätze fortschreiben, Freigaben erteilen (insb. Hochrisiko), Konflikte entscheiden, Reports überwachen.

**Rollen:** Use-Case-Owner, Model-Owner, Data Steward, DPO, Security Lead, Responsible Editor, Service Owner, Procurement Lead.

**Gremien & Gateways:** AIIA-Freigabe vor Go-Live; Change-Advisory-Board bei material Änderungen; jährliche Management-Reviews.

**RACI-Prinzip:** Klare Verantwortlichkeitszuordnung für jede Aktivität (Responsible, Accountable, Consulted, Informed).

---

## 4. Rechts- & Normenrahmen (EU-AI-Act, DSGVO, DSA, Urheberrecht, Gewerberecht)

**EU-AI-Act:** Risikobasierter Rahmen mit Verboten, Pflichten für Hochrisiko-Systeme, Dokumentation, Logging, Governance, Transparenzpflichten; gestaffelte Anwendbarkeit ab 2025/2026.

**DSGVO:** Rechtsgrundlagen (Art. 6/9), Betroffenenrechte, Privacy-by-Design/Default, Datenschutz-Folgenabschätzung (DPIA), Drittlandübermittlungen (Art. 44 ff.).

**DSA:** Plattformprozesse für Meldung, Beschwerde, Transparenzberichte, Risiko-Assessments großer Plattformen.

**Urheber- & Leistungsschutzrechte / Persönlichkeitsrechte:** Klare Lizenzketten, Bildnis-/Namensrechte, Hausrecht Dritter.

Branchenspezifische Vorgaben (z. B. Luftfahrt/Seerecht/Health) sind zusätzlich einzuhalten.

---

## 5. Risiko-Klassifikation & AI-Impact-Assessment (AIIA)

**Klassifikation:** (a) Verbotene Praktiken (nicht zulässig), (b) Hochrisiko-Systeme (strenge Pflichten), (c) begrenztes Risiko (Transparenz), (d) minimales Risiko.

**AIIA-Ablauf:** Beschreibung Zweck/Scope, Stakeholder, Rechtsgrundlagen, Datenquellen; Risikoanalyse (Recht, Ethik, Sicherheit, Bias, Umweltauswirkungen); Mitigationsplan; Entscheidung (AIEB-Freigabe).

**Re-Assessments:** Bei Material Changes, jährlich bei Hochrisiko; Dokumentation im zentralen Register.

---

## 6. Datenethik & Datenschutz (Rechtsgrundlagen, DPIA, Cookies, Drittland)

**Datenminimierung & Zweckbindung;** Pseudonymisierung/Anonymisierung bevorzugt.

**Transparenz:** Datenschutzhinweise, Auskunfts- und Löschwege; Portabilität; Widerspruchsmöglichkeiten.

**Cookies/Tracking:** Einwilligungs-Management; Widerruf; IP-Anonymisierung; nur freigegebene Tools.

**Drittlandtransfers:** Nur mit geeigneten Garantien (SCC/Angemessenheit); regelmäßige Prüfungen der Subprozessoren.

**DPIA:** Verpflichtend für risikoreiche Verarbeitung; technische/organisatorische Maßnahmen (TOMs) dokumentieren.

---

## 7. Modell- & Datenlebenszyklus (ML-Lifecycle, Data Cards, Model Cards)

**Data Lifecycle:** Acquisition, Curation, Labeling, Quality-Gates, Versionierung, Retention/Deletion.

**Model Lifecycle:** Problemdefinition, Architekturwahl, Training/Finetuning, Evaluation (Offline/Online), Freigabe, Betrieb, Monitoring, Retraining/Retirement.

**Data Cards:** Herkunft, Repräsentativität, Qualität, Bias-Befunde, Nutzungsbeschränkungen.

**Model Cards:** Zweck, Trainingsdaten, Benchmarks, Metriken, Limitationen, erwartete Fehlermuster, Do-/Don'ts.

**Provenienz & Reproduzierbarkeit:** Hashes, Daten-/Modell-Versionen, Pipeline-Nachweise.

---

## 8. Transparenz, Erklärbarkeit & Nutzerhinweise

Kennzeichnung bei KI-Interaktion und KI-generierten Inhalten.

**Erklärbarkeit:** Auf Use-Case zugeschnittene, laienverständliche Erklärungen (lokal/global).

**Nutzerhinweise:** Zweck, Haupteinflussfaktoren, Grenzen; Feedback- und Korrekturwege.

---

## 9. Human-in-the-Loop & Aufsichtspflichten

Menschliche Aufsicht als Standard bei relevanten Entscheidungen (insb. Hochrisiko).

Vier-Augen-Prinzip bei redaktionell/gesellschaftlich sensiblen Einsätzen.

Override-/Abbruch-Funktionen; Eskalationswege; Dokumentation.

---

## 10. Sicherheit, Robustheit & Red-Teaming (Prompt-Injection, Jailbreaks)

**Bedrohungsmodellierung (STRIDE + KI-spezifisch):** Prompt-Injection, Trainingsdatenvergiftung, Model Theft, Datenschutz-Leakage.

**Red-Teaming & adversarielle Tests;** Jailbreak-Prevention; Rate-Limiting; Output-Filter; Secret-Scanning.

**Robustheit:** Fallback-Prompts, Guardrails, Rollback-Pläne; Canary-Releases; Chaos-Tests für Safety.

---

## 11. Lieferkette, Menschenrechte & faire Arbeit (Modern Slavery, LkSG-analog)

**Menschenrechtliche Sorgfalt:** Risikoanalyse, Lieferantenkodex, vertragliche Zusagen, Audits, Abhilfe.

**Modern Slavery:** Jährliche Erklärung, Sensibilisierung, Meldewege.

**Arbeitsstandards:** Faire Entlohnung, Arbeitszeit, Gesundheitsschutz; Schutz von Hinweisgebenden.

---

## 12. Bias-Management, Fairness & Inklusion (gefährdete Kunden, Barrierefreiheit)

**Bias-Prüfungen:** Datensatzanalysen, Balancing, Diverse Testgruppen, Fairness-Metriken; dokumentierte Mitigation.

**Gefährdete Kunden:** Schutzziele, alternative Kanäle, klare Sprache; keine Ausnutzung kognitiver Schwächen.

**Barrierefreiheit:** WCAG-Konformität; Mehrsprachigkeit; inklusive Ansprache.

---

## 13. Generative KI, Herkunftsnachweise & Kennzeichnung (C2PA, Wasserzeichen)

**Kennzeichnung:** Sichtbare Label/Metadaten für KI-Inhalte; Hinweis bei Interaktionen.

**Herkunftsnachweise:** C2PA-Kontext, Signaturen/Wasserzeichen soweit technisch möglich.

**Urheberrechte/Leistungsschutz:** Lizenzen klären; Trainingsdaten-Compliance; Rechtekette dokumentieren.

---

## 14. Inhalte, Moderation & DSA-Prozesse (Meldung, Beschwerde, Transparenz)

**Meldewege:** Niedrigschwellige Nutzer-Meldung; priorisierte Bearbeitung illegaler Inhalte.

**Beschwerdeprozesse:** Transparente Begründung, Einspruch, Eskalation.

**Transparenzberichte:** Periodische Veröffentlichung relevanter Kennzahlen und Maßnahmen.

---

## 15. Domänenspezifischer Einsatz (News, Data, Health, Aviation, Yachts, Estate, Pay/Trade/Trust/Coin, Cars)

**News/Publishing:** Recherche-Assistenz, Übersetzung, Moderation; klare Kennzeichnung generativer Inhalte.

**SCANDIC DATA:** Sichere KI-/HPC-Infrastruktur, Mandantentrennung, HSM/KMS, Observability, Compliance-Artefakte.

**Health:** Evidenzbasierter Einsatz, menschliche Letztentscheidung, keine ungetesteten Diagnosen.

**Aviation/Yachts:** Safety-Prozesse, menschliche Aufsicht, Notfall-Prozeduren.

**Estate:** Bewertungsmodelle mit Fairness-Checks; ESG-Einbindung.

**Pay/Trade/Trust/Coin:** Betrugsprävention, KYC/AML, Marktüberwachung, erklärbare Entscheidungen.

**Cars:** Personalisierte Services mit striktem Datenschutz.

---

## 16. Drittparteien, Beschaffung & Vendor-Risk-Management

**Due-Diligence vor Onboarding:** Sicherheits-/Datenschutz-Niveau, Datenstandorte, Subprozessoren, Zertifikate.

**Verträge:** Audit-Rechte, Transparenz- und Abhilfe-Klauseln, SLA/OLA-Metriken.

**Monitoring:** Leistungs-KPIs, Befund-/Incident-Austausch, Exit-Pläne.

---

## 17. Betrieb, Observability, Notfall- & Wiederanlaufpläne

**Betrieb:** Observability (Logs, Metriken, Traces), SLO/SLI-Management, Kapazitätsplanung.

**Notfall:** Runbooks, DR-Tests, Wiederherstellungszeiten, Kommunikationspläne.

**Konfigurations-/Geheimnis-Management:** Least-Privilege, Rotationen, Härtung.

---

## 18. Vorfälle & Abhilfe (Ethik, Datenschutz, Sicherheit)

**Ethik-Incidents:** Unerwünschte Diskriminierung, Desinformation, unklare Herkunft – Sofortmaßnahmen und AIEB-Review.

**Datenschutz-Incidents:** Meldeprozesse an DPO/Aufsicht; Betroffenen-Info; Ursachenanalyse.

**Sicherheits-Incidents:** CSIRT-Abläufe, Forensik, Lessons Learned, Präventionsmaßnahmen.

---

## 19. Metriken, KPIs & Assurance (intern/extern)

**Pflicht-KPIs:** 100% AIIA-Abdeckung produktiver KI-Use-Cases; <14 Tage Median-Bearbeitungszeit Beschwerden; >95% Trainingsquote; 0 offene kritische Audit-Findings.

**Fairness-Metriken:** Disparate Impact, Equalized Odds (Use-Case-spezifisch).

**Nachhaltigkeit:** Energie-/PUE/Kohlenstoff-Kennzahlen der Rechenzentren; Effizienz der Modelle.

---

## 20. Schulung, Awareness & Kulturwandel

**Verpflichtende Trainings (jährlich):** KI-Ethik, Datenschutz, Sicherheit, Medienethik; zielgruppenspezifische Module.

**Awareness-Kampagnen:** Leitfäden, Brown-Bag-Sessions, Sprechstunden; interne Communities of Practice.

**Kultur:** Vorbildfunktion der Führung, Fehlerkultur, Belohnung verantwortungsvollen Handelns.

---

## 21. Umsetzung & Roadmap (0–6 / 6–12 / 12–24 Monate)

**0–6 Monate:** Inventur KI-Use-Cases; AIIA-Prozess; Mindestkontrollen; Schulungswelle; Supplier-Screening.

**6–12 Monate:** Red-Teaming ausrollen; erste Transparenzberichte; Energie-Programm; RACI finalisieren.

**12–24 Monate:** ISO/IEC-42001-Ausrichtung; Limited Assurance; kontinuierliche Verbesserung; CSRD/ESRS-Vorbereitung (falls anwendbar).

---

## 22. Rollen & RACI-Matrix

**Use-Case-Owner (A):** Zweck, Nutzen, KPIs, Budget, Re-Assessments.

**Model-Owner (R):** Daten/Training/Eval, Model Card, Drift-Monitoring.

**DPO (C/A bei Datenschutz):** Rechtsgrundlage, DPIA, Betroffenenrechte.

**Security Lead (C):** Bedrohungsmodellierung, Red-Teaming, TOMs.

**Responsible Editor (C):** Medienethik, Kennzeichnungen, Korrekturregister.

**Service Owner (R):** Betrieb, SLO, Incident-Management.

**Procurement Lead (R/C):** Drittparteien, Verträge, Exit-Pläne.

---

## 23. Checklisten (AIIA-Kurz, Datenfreigabe, Go-Live-Gate)

**AIIA-Kurzcheck:** Zweck? Rechtsgrundlage? Betroffene? Risiken (Recht/Ethik/Security/Bias/Umwelt)? Mitigation? HIL-Kontrollen?

**Datenfreigabe:** Quelle rechtmäßig? Minimierung? Retention? Zugriff? Drittland?

**Go-Live-Gate:** Artefakte vollständig (Data/Model Cards, Logs)? Red-Team-Ergebnisse adressiert? Monitoring/DR eingerichtet?

---

## 24. Formblätter & Vorlagen (Model Card, Data Card, Incident Report)

**Model-Card-Template:** Zweck, Daten, Training, Benchmarks, Limitationen, Risiken, Verantwortliche, Kontakt.

**Data-Card-Template:** Herkunft, Lizenz, Qualität, Repräsentativität, Bias-Checks, Nutzungsbeschränkungen.

**Incident-Report-Template:** Ereignis, Auswirkungen, Betroffene, Sofortmaßnahmen, Root Cause, Abhilfe, Lessons Learned.

---

## 25. Glossar & Referenzen

**Glossar:** KI-System, Generative KI, Hochrisiko-System, AIIA, HIL, C2PA, Red-Teaming, DPIA, RACI, SLO/SLI.

**Referenzen:** EU-AI-Act, DSGVO, DSA, OECD-AI-Principles, NIST AI RMF, ISO/IEC 42001, interne Richtlinien (Datenschutz, DSA-Prozesse, Modern Slavery, Sustainability).
  `
};

const contentEN = {
  title: "AI Code of Ethics",
  back: "Back",
  lastUpdated: "Date: 1 January 2026",
  content: `
## SCANDICDEV's AI Code of Ethics powered by "Scandic Pilot" Library – Key Areas & Direct Links

- 1. Preamble & Scope
- 2. Core Values & Guiding Principles
- 3. Governance & Responsibilities (AI Ethics Board, RACI)
- 4. Legal & Regulatory Framework (EU AI Act, GDPR, DSA, Copyright Law, Commercial Law)
- 5. Risk Classification & AI Impact Assessment (AIIA)
- 6. Data Ethics & Data Protection (Legal Basis, DPIA, Cookies, Third Countries)
- 7. Model & Data Lifecycle (ML Lifecycle, Data Cards, Model Cards)
- 8. Transparency, explainability & user notices
- 9. Human-in-the-loop & supervisory obligations
- 10. Security, Robustness & Red Teaming (prompt injection, jailbreaks)
- 11. Supply Chain, Human Rights & Fair Labour (Modern Slavery, LkSG-analogous)
- 12. Bias management, fairness & inclusion (vulnerable customers, accessibility)
- 13. Generative AI, proof of origin & labelling (C2PA, watermarks)
- 14. Content, moderation & DSA processes (reporting, complaints, transparency)
- 15. Domain-specific application (News, Data, Health, Aviation, Yachts, Estate, Pay/Trade/Trust/Coin, Cars)
- 16. Third parties, procurement & vendor risk management
- 17. Operations, observability, contingency and recovery plans
- 18. Incidents & Remedial Action (Ethics, Data Protection, Security)
- 19. Metrics, KPIs & assurance (internal/external)
- 20. Training, awareness & cultural change
- 21. Implementation & Roadmap (0–6 / 6–12 / 12–24 months)
- 22. Roles & RACI matrix (excerpt)
- 23. Checklists (AIIA summary, data release, go-live gate)
- 24. Forms & Templates (Model Card, Data Card, Incident Report)
- 25. Glossary (excerpt) & References

---

## 1. Preamble & Scope

This Code sets out binding principles, processes and controls for the development, procurement, operation and use of AI within the LEGIER Group by SCANDIC DATA. It applies across the Group to employees, managers, data processors, suppliers and partners. It integrates existing group policies (data protection, digital services processes, corporate governance, sustainability, human rights policy, modern slavery statement) and expands them to include AI-specific requirements. The aim is to enable benefits and innovation, make risks manageable, and safeguard the rights of users, customers and the general public.

---

## 2. Core Values & Guiding Principles

Human dignity and fundamental rights take precedence over economic efficiency. AI serves people – never the other way round.

**Legal compliance:** Adherence to the EU AI Act, GDPR, DSA and sector-specific standards. No use of prohibited practices.

**Responsibility & Accountability:** A responsible owner is designated for every AI system; decisions are transparent and subject to challenge.

**Proportionality:** Balance between purpose, risk, intensity of intervention and societal impact.

**Transparency & explainability:** Appropriate guidance, documentation and communication channels regarding how the system works, data sources and limitations.

**Fairness & Inclusion:** Systematic bias testing, protection of vulnerable groups, accessibility and multilingualism.

**Security & Resilience:** Security by design, defence in depth, continuous hardening and monitoring.

**Sustainability:** Efficiency of models and data centres (energy, PUE/CFE), lifecycle perspective on data/models.

---

## 3. Governance & Responsibilities (AI Ethics Board, RACI)

**AI Ethics Board (AIEB):** Interdisciplinary (Tech, Legal/Compliance, Data Protection, Security, Editorial/Product, People). Responsibilities: Updating principles, granting approvals (particularly for high-risk cases), resolving conflicts, monitoring reports.

**Roles:** Use Case Owner, Model Owner, Data Steward, DPO, Security Lead, Responsible Editor, Service Owner, Procurement Lead.

**Committees & Gateways:** AIIA approval prior to go-live; Change Advisory Board for material changes; annual management reviews.

**RACI principle:** Clear assignment of responsibility for each activity (Responsible, Accountable, Consulted, Informed).

---

## 4. Legal & regulatory framework (EU AI Act, GDPR, DSA, copyright law, commercial law)

**EU AI Act:** A risk-based framework with prohibitions, obligations for high-risk systems, documentation, logging, governance and transparency requirements; phased implementation from 2025/2026.

**GDPR:** Legal bases (Art. 6/9), data subjects' rights, privacy by design/default, data protection impact assessment (DPIA), transfers to third countries (Art. 44 et seq.).

**DSA:** Platform processes for reporting, complaints, transparency reports, risk assessments of large platforms.

**Copyright & related rights / personality rights:** Clear licence chains, image/name rights, third-party property rights.

Industry-specific requirements (e.g. aviation/maritime law/health) must also be complied with.

---

## 5. Risk classification & AI impact assessment (AIIA)

**Classification:** (a) Prohibited practices (not permitted), (b) High-risk systems (strict obligations), (c) Limited risk (transparency), (d) Minimal risk.

**AIIA process:** Description of purpose/scope, stakeholders, legal basis, data sources; risk analysis (legal, ethical, security, bias, environmental impact); mitigation plan; decision (AIEB approval).

**Re-assessments:** In the event of material changes; annually for high-risk cases; documentation in the central register.

---

## 6. Data ethics & data protection (legal basis, DPIA, cookies, third countries)

**Data minimisation & purpose limitation;** pseudonymisation/anonymisation preferred.

**Transparency:** privacy notices, procedures for access and erasure; portability; options to object.

**Cookies/tracking:** Consent management; withdrawal; IP anonymisation; only approved tools.

**Transfers to third countries:** Only with appropriate safeguards (SCCs/adequacy); regular audits of sub-processors.

**DPIA:** Mandatory for high-risk processing; document technical and organisational measures (TOMs).

---

## 7. Model & Data Lifecycle (ML Lifecycle, Data Cards, Model Cards)

**Data Lifecycle:** Acquisition, Curation, Labelling, Quality Gates, Versioning, Retention/Deletion.

**Model Lifecycle:** Problem definition, Architecture selection, Training/Fine-tuning, Evaluation (offline/online), Release, Operation, Monitoring, Retraining/Retirement.

**Data Cards:** Origin, representativeness, quality, bias findings, usage restrictions.

**Model Cards:** Purpose, training data, benchmarks, metrics, limitations, expected error patterns, dos and don'ts.

**Provenance & reproducibility:** hashes, data/model versions, pipeline records.

---

## 8. Transparency, explainability & user notices

Labelling for AI interaction and AI-generated content.

**Explainability:** Explanations tailored to the use case and understandable to the general public (local/global).

**User notices:** Purpose, main influencing factors, limitations; feedback and correction channels.

---

## 9. Human-in-the-loop & supervisory duties

Human oversight as standard for relevant decisions (especially high-risk).

Dual-control principle for editorially or socially sensitive applications.

Override/cancellation functions; escalation procedures; documentation.

---

## 10. Security, robustness & red teaming (prompt injection, jailbreaks)

**Threat modelling (STRIDE + AI-specific):** prompt injection, training data poisoning, model theft, data leakage.

**Red teaming & adversarial testing;** jailbreak prevention; rate limiting; output filters; secret scanning.

**Robustness:** fallback prompts, guardrails, rollback plans; canary releases; chaos testing for safety.

---

## 11. Supply chain, human rights & fair labour (Modern Slavery, LkSG-analogous)

**Human rights due diligence:** risk analysis, supplier code of conduct, contractual commitments, audits, remedial action.

**Modern Slavery:** Annual Statement, Awareness-raising, Reporting Channels.

**Labour standards:** Fair pay, working hours, health and safety; protection of whistleblowers.

---

## 12. Bias management, fairness & inclusion (vulnerable customers, accessibility)

**Bias assessments:** Data set analyses, balancing, diverse test groups, fairness metrics; documented mitigation.

**Vulnerable customers:** Protection objectives, alternative channels, plain language; no exploitation of cognitive impairments.

**Accessibility:** WCAG compliance; multilingualism; inclusive language.

---

## 13. Generative AI, proof of origin & labelling (C2PA, watermarks)

**Labelling:** Visible labels/metadata for AI content; notification during interactions.

**Proof of origin:** C2PA context, signatures/watermarks where technically feasible.

**Copyright/neighbouring rights:** Clarify licences; training data compliance; document rights chain.

---

## 14. Content, moderation & DSA processes (reporting, complaints, transparency)

**Reporting channels:** Low-threshold user reporting; prioritised handling of illegal content.

**Complaints procedures:** Transparent reasoning, appeal, escalation.

**Transparency reports:** Periodic publication of relevant key figures and measures.

---

## 15. Domain-specific applications (News, Data, Health, Aviation, Yachts, Estate, Pay/Trade/Trust/Coin, Cars)

**News/Publishing:** Research assistance, translation, moderation; clear labelling of generative content.

**SCANDIC DATA:** Secure AI/HPC infrastructure, client isolation, HSM/KMS, observability, compliance artefacts.

**Health:** Evidence-based use, human final decision, no untested diagnoses.

**Aviation/Yachts:** Safety processes, human oversight, emergency procedures.

**Estate:** Valuation models with fairness checks; ESG integration.

**Pay/Trade/Trust/Coin:** Fraud prevention, KYC/AML, market surveillance, explainable decisions.

**Cars:** Personalised services with strict data protection.

---

## 16. Third parties, procurement & vendor risk management

**Due diligence prior to onboarding:** security/data protection levels, data locations, sub-processors, certificates.

**Contracts:** Audit rights, transparency and remedy clauses, SLA/OLA metrics.

**Monitoring:** Performance KPIs, findings/incident sharing, exit plans.

---

## 17. Operations, observability, contingency and recovery plans

**Operations:** Observability (logs, metrics, traces), SLO/SLI management, capacity planning.

**Emergency:** Runbooks, DR tests, recovery times, communication plans.

**Configuration/secret management:** least privilege, rotations, hardening.

---

## 18. Incidents & Remediation (Ethics, Data Protection, Security)

**Ethics incidents:** Unwanted discrimination, disinformation, unclear origin – immediate measures and AIEB review.

**Data protection incidents:** Reporting processes to DPO/supervisory authority; information for data subjects; root cause analysis.

**Security incidents:** CSIRT procedures, forensics, lessons learnt, preventive measures.

---

## 19. Metrics, KPIs & Assurance (internal/external)

**Mandatory KPIs:** 100% AIIA coverage of productive AI use cases; <14 days median processing time for complaints; >95% training rate; 0 open critical audit findings.

**Fairness metrics:** Disparate impact, equalised odds (use-case specific).

**Sustainability:** Energy/PUE/carbon metrics of data centres; model efficiency.

---

## 20. Training, Awareness & Cultural Change

**Mandatory training (annual):** AI ethics, data protection, security, media ethics; target group-specific modules.

**Awareness campaigns:** guidelines, brown-bag sessions, consultation hours; internal communities of practice.

**Culture:** Leadership setting an example, culture of learning from mistakes, rewarding responsible behaviour.

---

## 21. Implementation & Roadmap (0–6 / 6–12 / 12–24 months)

**0–6 months:** Inventory of AI use cases; AIIA process; minimum controls; training programme; supplier screening.

**6–12 months:** Roll out red teaming; first transparency reports; energy programme; finalise RACI.

**12–24 months:** Alignment with ISO/IEC 42001; limited assurance; continuous improvement; CSRD/ESRS preparation (where applicable).

---

## 22. Roles & RACI Matrix

**Use Case Owner (A):** Purpose, benefits, KPIs, budget, reassessments.

**Model Owner (R):** Data/training/evaluation, model card, drift monitoring.

**DPO (C/A for data protection):** Legal basis, DPIA, data subject rights.

**Security Lead (C):** Threat modelling, red teaming, TOMs.

**Responsible Editor (C):** Media ethics, labelling, correction register.

**Service Owner (R):** Operations, SLO, incident management.

**Procurement Lead (R/C):** Third parties, contracts, exit plans.

---

## 23. Checklists (AIIA Summary, Data Release, Go-Live Gate)

**AIIA Quick Check:** Purpose? Legal basis? Data subjects? Risks (legal/ethical/security/bias/environmental)? Mitigation? HIL controls?

**Data release:** Is the source lawful? Minimisation? Retention? Access? Third country?

**Go-Live Gate:** Are artefacts complete (Data/Model Cards, Logs)? Have Red Team results been addressed? Has monitoring/DR been set up?

---

## 24. Forms & Templates (Model Card, Data Card, Incident Report)

**Model Card Template:** Purpose, data, training, benchmarks, limitations, risks, responsible parties, contact.

**Data Card Template:** Origin, licence, quality, representativeness, bias checks, usage restrictions.

**Incident Report Template:** Event, Impact, Affected Parties, Immediate Actions, Root Cause, Remedial Action, Lessons Learned.

---

## 25. Glossary & References

**Glossary:** AI system, generative AI, high-risk system, AIIA, HIL, C2PA, red-teaming, DPIA, RACI, SLO/SLI.

**References:** EU AI Act, GDPR, DSA, OECD AI Principles, NIST AI RMF, ISO/IEC 42001, internal guidelines (data protection, DSA processes, modern slavery, sustainability).
  `
};

export default function AIEthics() {
  const { language } = useLanguage();
  const location = useLocation();
  const content = language === 'de' ? contentDE : contentEN;
  const alternateLanguagePath = getAlternateLanguagePath(location.pathname);
  const homePath = getHomePath(language);
  const seo = legalPageSeo[location.pathname];

  const renderContent = (markdown) => {
    const lines = markdown.split('\n');
    const elements = [];
    let key = 0;

    for (const line of lines) {
      if (line.startsWith('## ')) {
        elements.push(
          <h2 key={key++} className="text-2xl font-bold text-white mt-10 mb-4 font-['Space_Grotesk']">
            {line.replace('## ', '')}
          </h2>
        );
      } else if (line.startsWith('### ')) {
        elements.push(
          <h3 key={key++} className="text-xl font-semibold text-violet-400 mt-8 mb-3 font-['Space_Grotesk']">
            {line.replace('### ', '')}
          </h3>
        );
      } else if (line.startsWith('**') && line.endsWith('**')) {
        elements.push(
          <p key={key++} className="text-white font-semibold mt-4 mb-2">
            {line.replace(/\*\*/g, '')}
          </p>
        );
      } else if (line.startsWith('- **') || line.startsWith('- ')) {
        const text = line.replace('- ', '');
        const parts = text.split(/(\*\*.*?\*\*)/g);
        elements.push(
          <div key={key++} className="mb-2 pl-4 flex">
            <span className="text-violet-400 mr-2 flex-shrink-0">&bull;</span>
            <span className="text-zinc-300">
              {parts.map((part, i) =>
                part.startsWith('**') && part.endsWith('**')
                  ? <strong key={i} className="text-white">{part.replace(/\*\*/g, '')}</strong>
                  : part
              )}
            </span>
          </div>
        );
      } else if (line === '---') {
        // Skip dividers
      } else if (line.trim() === '') {
        // Skip empty lines
      } else {
        // Handle inline bold
        const parts = line.split(/(\*\*.*?\*\*)/g);
        elements.push(
          <p key={key++} className="text-zinc-300 mb-3 leading-relaxed">
            {parts.map((part, i) =>
              part.startsWith('**') && part.endsWith('**')
                ? <strong key={i} className="text-white">{part.replace(/\*\*/g, '')}</strong>
                : part
            )}
          </p>
        );
      }
    }

    return elements;
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-zinc-300">
      <Seo
        title={seo?.title || content.title}
        description={seo?.description || content.title}
        path={location.pathname}
        language={language}
      />
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#0A0A0B]/85 border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-3 h-20 md:h-24">
            <Link to={homePath} aria-label={content.back} className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">{content.back}</span>
            </Link>
            <ScandicDevLogo className="h-14 w-auto object-contain md:h-16" />
            <LanguageToggleLink language={language} href={alternateLanguagePath} />
          </div>
        </div>
      </header>

      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <article className="max-w-4xl mx-auto">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4 font-['Space_Grotesk']">{content.title}</h1>
          {renderContent(content.content)}
          <p className="text-sm text-zinc-500 mt-12 pt-8 border-t border-zinc-800">{content.lastUpdated}</p>
        </article>
      </main>
    </div>
  );
}
